/**
 * verbs.ts: the one verb table narration reads.
 *
 * A step's words are often a command: the spine writes "Read the Forbes
 * article", a plan item says "Check the totals", a command carries its own
 * description ("List files in the repo"). A step that is still going says what
 * is being done ("Listing files in the repo"); a step that has settled says
 * what was done ("Listed files in the repo"). Only verbs in the table turn: a
 * first word it does not know (a name, a query, a tool) is left alone, and the
 * caller says the words as written. Narration never guesses a verb form.
 *
 * Each verb carries both forms, written out: English past tense has too many
 * irregulars ("ran", "found", "wrote") and doublings ("stopped", "planned") to
 * derive them safely.
 */

export type Tense = "progressive" | "past";

// verb → [progressive, past]
const VERBS: ReadonlyMap<string, readonly [string, string]> = new Map(
  Object.entries({
    add: ["adding", "added"],
    analyze: ["analyzing", "analyzed"],
    analyse: ["analysing", "analysed"],
    apply: ["applying", "applied"],
    ask: ["asking", "asked"],
    book: ["booking", "booked"],
    browse: ["browsing", "browsed"],
    build: ["building", "built"],
    cancel: ["cancelling", "cancelled"],
    capture: ["capturing", "captured"],
    check: ["checking", "checked"],
    clean: ["cleaning", "cleaned"],
    click: ["clicking", "clicked"],
    close: ["closing", "closed"],
    collect: ["collecting", "collected"],
    compare: ["comparing", "compared"],
    compile: ["compiling", "compiled"],
    compose: ["composing", "composed"],
    confirm: ["confirming", "confirmed"],
    connect: ["connecting", "connected"],
    convert: ["converting", "converted"],
    copy: ["copying", "copied"],
    count: ["counting", "counted"],
    create: ["creating", "created"],
    deliver: ["delivering", "delivered"],
    deploy: ["deploying", "deployed"],
    delete: ["deleting", "deleted"],
    draft: ["drafting", "drafted"],
    download: ["downloading", "downloaded"],
    edit: ["editing", "edited"],
    email: ["emailing", "emailed"],
    export: ["exporting", "exported"],
    extract: ["extracting", "extracted"],
    fetch: ["fetching", "fetched"],
    fill: ["filling", "filled"],
    filter: ["filtering", "filtered"],
    find: ["finding", "found"],
    finish: ["finishing", "finished"],
    fix: ["fixing", "fixed"],
    format: ["formatting", "formatted"],
    gather: ["gathering", "gathered"],
    generate: ["generating", "generated"],
    get: ["getting", "got"],
    go: ["going", "went"],
    import: ["importing", "imported"],
    install: ["installing", "installed"],
    list: ["listing", "listed"],
    load: ["loading", "loaded"],
    log: ["logging", "logged"],
    look: ["looking", "looked"],
    make: ["making", "made"],
    mark: ["marking", "marked"],
    merge: ["merging", "merged"],
    move: ["moving", "moved"],
    navigate: ["navigating", "navigated"],
    note: ["noting", "noted"],
    open: ["opening", "opened"],
    parse: ["parsing", "parsed"],
    plan: ["planning", "planned"],
    post: ["posting", "posted"],
    prepare: ["preparing", "prepared"],
    publish: ["publishing", "published"],
    pull: ["pulling", "pulled"],
    push: ["pushing", "pushed"],
    put: ["putting", "put"],
    read: ["reading", "read"],
    record: ["recording", "recorded"],
    register: ["registering", "registered"],
    remove: ["removing", "removed"],
    rename: ["renaming", "renamed"],
    reply: ["replying", "replied"],
    research: ["researching", "researched"],
    resolve: ["resolving", "resolved"],
    review: ["reviewing", "reviewed"],
    run: ["running", "ran"],
    save: ["saving", "saved"],
    scan: ["scanning", "scanned"],
    schedule: ["scheduling", "scheduled"],
    search: ["searching", "searched"],
    select: ["selecting", "selected"],
    send: ["sending", "sent"],
    serve: ["serving", "served"],
    set: ["setting", "set"],
    share: ["sharing", "shared"],
    show: ["showing", "showed"],
    sort: ["sorting", "sorted"],
    start: ["starting", "started"],
    stop: ["stopping", "stopped"],
    submit: ["submitting", "submitted"],
    summarise: ["summarising", "summarised"],
    summarize: ["summarizing", "summarized"],
    sync: ["syncing", "synced"],
    take: ["taking", "took"],
    test: ["testing", "tested"],
    try: ["trying", "tried"],
    type: ["typing", "typed"],
    update: ["updating", "updated"],
    upload: ["uploading", "uploaded"],
    use: ["using", "used"],
    verify: ["verifying", "verified"],
    visit: ["visiting", "visited"],
    wait: ["waiting", "waited"],
    watch: ["watching", "watched"],
    write: ["writing", "wrote"],
    // The verbs the spine reaches for as a task wraps up (staging drill
    // 0dd0b38e, 09-03: "Present the finished news summary", "Hand over the
    // completed file"), and their common companions.
    present: ["presenting", "presented"],
    hand: ["handing", "handed"],
    finalize: ["finalizing", "finalized"],
    finalise: ["finalising", "finalised"],
    complete: ["completing", "completed"],
    wrap: ["wrapping", "wrapped"],
    report: ["reporting", "reported"],
    respond: ["responding", "responded"],
    answer: ["answering", "answered"],
    explain: ["explaining", "explained"],
    describe: ["describing", "described"],
    outline: ["outlining", "outlined"],
    identify: ["identifying", "identified"],
    locate: ["locating", "located"],
    retrieve: ["retrieving", "retrieved"],
    store: ["storing", "stored"],
    organize: ["organizing", "organized"],
    organise: ["organising", "organised"],
    rank: ["ranking", "ranked"],
    choose: ["choosing", "chose"],
    pick: ["picking", "picked"],
    decide: ["deciding", "decided"],
    determine: ["determining", "determined"],
    calculate: ["calculating", "calculated"],
    compute: ["computing", "computed"],
    estimate: ["estimating", "estimated"],
    measure: ["measuring", "measured"],
    monitor: ["monitoring", "monitored"],
    track: ["tracking", "tracked"],
    examine: ["examining", "examined"],
    inspect: ["inspecting", "inspected"],
    explore: ["exploring", "explored"],
    investigate: ["investigating", "investigated"],
    evaluate: ["evaluating", "evaluated"],
    assess: ["assessing", "assessed"],
    validate: ["validating", "validated"],
    translate: ["translating", "translated"],
    transcribe: ["transcribing", "transcribed"],
    rewrite: ["rewriting", "rewrote"],
    refine: ["refining", "refined"],
    polish: ["polishing", "polished"],
    improve: ["improving", "improved"],
    tidy: ["tidying", "tidied"],
    archive: ["archiving", "archived"],
    attach: ["attaching", "attached"],
    forward: ["forwarding", "forwarded"],
    invite: ["inviting", "invited"],
    notify: ["notifying", "notified"],
    message: ["messaging", "messaged"],
    call: ["calling", "called"],
    order: ["ordering", "ordered"],
    buy: ["buying", "bought"],
    pay: ["paying", "paid"],
    begin: ["beginning", "began"],
    continue: ["continuing", "continued"],
    resume: ["resuming", "resumed"],
    retry: ["retrying", "retried"],
    restart: ["restarting", "restarted"],
    launch: ["launching", "launched"],
    execute: ["executing", "executed"],
    process: ["processing", "processed"],
    handle: ["handling", "handled"],
    manage: ["managing", "managed"],
    configure: ["configuring", "configured"],
    enable: ["enabling", "enabled"],
    disable: ["disabling", "disabled"],
    switch: ["switching", "switched"],
    assign: ["assigning", "assigned"],
    label: ["labelling", "labelled"],
    tag: ["tagging", "tagged"],
    flag: ["flagging", "flagged"],
    highlight: ["highlighting", "highlighted"],
    annotate: ["annotating", "annotated"],
    comment: ["commenting", "commented"],
    print: ["printing", "printed"],
    scrape: ["scraping", "scraped"],
    crawl: ["crawling", "crawled"],
    aggregate: ["aggregating", "aggregated"],
    combine: ["combining", "combined"],
    join: ["joining", "joined"],
    split: ["splitting", "split"],
    group: ["grouping", "grouped"],
    map: ["mapping", "mapped"],
    plot: ["plotting", "plotted"],
    chart: ["charting", "charted"],
    draw: ["drawing", "drew"],
    design: ["designing", "designed"],
    sketch: ["sketching", "sketched"],
    implement: ["implementing", "implemented"],
    debug: ["debugging", "debugged"],
    refactor: ["refactoring", "refactored"],
    optimize: ["optimizing", "optimized"],
    migrate: ["migrating", "migrated"],
    restore: ["restoring", "restored"],
    recover: ["recovering", "recovered"],
    repair: ["repairing", "repaired"],
    patch: ["patching", "patched"],
    upgrade: ["upgrading", "upgraded"],
    uninstall: ["uninstalling", "uninstalled"],
    reset: ["resetting", "reset"],
    clear: ["clearing", "cleared"],
    insert: ["inserting", "inserted"],
    append: ["appending", "appended"],
    replace: ["replacing", "replaced"],
    swap: ["swapping", "swapped"],
    scroll: ["scrolling", "scrolled"],
    press: ["pressing", "pressed"],
    tap: ["tapping", "tapped"],
    enter: ["entering", "entered"],
    sign: ["signing", "signed"],
    authenticate: ["authenticating", "authenticated"],
    authorize: ["authorizing", "authorized"],
    grant: ["granting", "granted"],
    request: ["requesting", "requested"],
    tell: ["telling", "told"],
    inform: ["informing", "informed"],
    remind: ["reminding", "reminded"],
    alert: ["alerting", "alerted"],
    approve: ["approving", "approved"],
    reject: ["rejecting", "rejected"],
    accept: ["accepting", "accepted"],
    decline: ["declining", "declined"],
    pause: ["pausing", "paused"],
    hold: ["holding", "held"],
    queue: ["queueing", "queued"],
    poll: ["polling", "polled"],
    listen: ["listening", "listened"],
    observe: ["observing", "observed"],
    detect: ["detecting", "detected"],
    classify: ["classifying", "classified"],
    categorize: ["categorizing", "categorized"],
    match: ["matching", "matched"],
    diff: ["diffing", "diffed"],
    contrast: ["contrasting", "contrasted"],
    benchmark: ["benchmarking", "benchmarked"],
    audit: ["auditing", "audited"],
    probe: ["probing", "probed"],
    // The verbs a command's own description opens with (the coding engine's
    // Bash tool asks for one on every call: "List files in the repo").
    change: ["changing", "changed"],
    clone: ["cloning", "cloned"],
    commit: ["committing", "committed"],
    compress: ["compressing", "compressed"],
    display: ["displaying", "displayed"],
    ensure: ["ensuring", "ensured"],
    kill: ["killing", "killed"],
    lint: ["linting", "linted"],
    query: ["querying", "queried"],
    rebuild: ["rebuilding", "rebuilt"],
    reinstall: ["reinstalling", "reinstalled"],
    render: ["rendering", "rendered"],
    rerun: ["rerunning", "reran"],
    resize: ["resizing", "resized"],
    unpack: ["unpacking", "unpacked"],
    unzip: ["unzipping", "unzipped"],
    view: ["viewing", "viewed"],
    zip: ["zipping", "zipped"],
  } satisfies Record<string, readonly [string, string]>)
);

const upperFirst = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/** A word's form in the tense asked, in the case it came in; null when the
 *  table does not know it as a verb. */
export function verbForm(word: string, tense: Tense): string | null {
  const forms = VERBS.get(word.toLowerCase());
  if (!forms) return null;
  const turned = forms[tense === "progressive" ? 0 : 1];
  return word.charAt(0) === word.charAt(0).toUpperCase()
    ? upperFirst(turned)
    : turned;
}

/**
 * A command in the tense asked: its first verb turns, and so does the second
 * verb of a pair ("Build and test the app" → "Built and tested the app").
 * Null when the first word is not a verb the table knows.
 */
export function tensed(sentence: string, tense: Tense): string | null {
  const words = sentence.split(" ");
  const first = verbForm(words[0] ?? "", tense);
  if (!first) return null;
  words[0] = first;
  if (words[1]?.toLowerCase() === "and" && words[2])
    words[2] = verbForm(words[2], tense) ?? words[2];
  return words.join(" ");
}
